import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { IRedeemableAttachedConfiguration } from "./Configurations";
import type {
  IListRedeemables,
  IListRedeemablesParams,
  IMenuItemRestriction,
} from "./Redeemables";
import { RedeemablesTypes } from "@/components/screens/Redeemables/Shared/Types/ListRedeemablesTypes";
import type { DiscountType } from "./Discount";

export enum PrepaidRechargeType {
  Normal = 0,
  Rechargeable = 1,
}

export enum TabStatus {
  AwaitingPayment = 0,
  Paid = 1,
  Completed = 2,
}

export enum RedeemableApplicable {
  OnPremise = 0,
  Collection = 1,
  All = 2,
  Delivery = 3,
}

export interface IListTab {
  active: boolean;
  applicable_to: RedeemableApplicable;
  balance: number;
  cap: number;
  code: string;
  email: string;
  id: number;
  identifier: string;
  message: string;
  customer_message?: string | null;
  name: string;
  status: TabStatus;
  applies_to_service_charge?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ITab extends IListTab {
  is_applicable: boolean;
  applicable_weekdays: Array<number>;
  cap_per_head: number;
  daily_redemptions_cap: number | null;
  discounted_menu_only: boolean;
  bill: unknown;
  configuration: IRedeemableAttachedConfiguration;
  menu_item_restrictions: Array<IMenuItemRestriction>;
  percentage: number;
  restaurant_id: ID;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  timezone: string;
  discount_type: DiscountType;
  recharge_type: PrepaidRechargeType;
  nano_id: string;
  recipient_email: string;
  recipient_name: string;
}

export interface IListTabParams {
  search?: string;
  isVenueSpecific?: boolean;
  page?: number;
  page_size?: number;
}

export interface IListTabResponse extends APIPaginatedResponse<IListTab> {}

export interface IListTabResponseAll {
  pages: Array<IListTabResponse>;
  pageParams: number[];
}

export interface IRetrieveTabParams {
  id: ID;
}

export interface IDeleteTabParams extends IRetrieveTabParams {}

export interface ICreateTabParams {
  code: string;
  message: string;
  customer_message?: string | null;
  start_date: string;
  end_date: string;
  recharge_type: PrepaidRechargeType;
  email: string;
  name: string;
  cap: number;
  active: boolean;
  applicable_weekdays: Array<number>;
  daily_redemptions_cap?: number | null;
}

export interface IUpdateTabParams extends Omit<ICreateTabParams, "cap"> {
  id: ID;
  start_time: string;
  end_time: string;
}

export interface ITabService {
  list: (params?: IListTabParams) => UseQueryResult<IListTabResponse, Error>;
  listAll: (
    params?: IListTabParams,
  ) => UseInfiniteQueryResult<IListTabResponseAll, Error>;
  retrieve: (params: IRetrieveTabParams) => UseQueryResult<ITab, Error>;
  create: (
    listParams?: IListRedeemablesParams,
  ) => UseMutationResult<ITab, Error, ICreateTabParams>;
  update: () => UseMutationResult<ITab, Error, IUpdateTabParams>;
  partialUpdate: () => UseMutationResult<
    ITab,
    Error,
    Partial<IUpdateTabParams>
  >;
  delete: () => UseMutationResult<boolean, Error, IDeleteTabParams>;
}

export class TabService implements ITabService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params?: IListTabParams): UseQueryResult<IListTabResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );
    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const {
          data: { results, next, count, previous },
        } = await this.api.get<AxiosResponse<IListTabResponse>>(
          "/promotions/tabs/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: restParams,
          },
        );

        return {
          results,
          next,
          count,
          previous,
        };
      },
    });
  }

  listAll(
    params?: IListTabParams,
  ): UseInfiniteQueryResult<IListTabResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;

    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "list-all"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<AxiosResponse<IListTabResponse>>(
          "/promotions/tabs/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: { ...restParams, page: pageParam, page_size: 50 },
          },
        );

        return data;
      },
      getNextPageParam: (lastPage: IListTabResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  retrieve(params: IRetrieveTabParams): UseQueryResult<ITab, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "retrieve", params.id],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<ITab>>(
          `/promotions/tabs/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      enabled: !!params.id,
    });
  }

  create(
    listParams?: IListRedeemablesParams,
  ): UseMutationResult<ITab, Error, ICreateTabParams> {
    const queryClient = useQueryClient();
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams || {}).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<ITab>>(
          "/promotions/tabs/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newTab) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
        const previousTabs = queryClient.getQueryData<IListRedeemables>(
          buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        );

        if (previousTabs) {
          queryClient.setQueryData<IListRedeemables>(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            (old) => {
              return {
                ...old,
                results: [
                  ...old.results,
                  {
                    id: -1,
                    active: true,
                    redeemable_type: RedeemablesTypes.PrepaidTab,
                    start_date: newTab.start_date,
                    configuration_id: null,
                    end_date: newTab.end_date,
                    message: newTab?.message || "",
                    code: newTab.code,
                  },
                ],
                count: old.count + 1,
                next: old.next,
                previous: old.previous,
              };
            },
          );
        }

        return { previousTabs };
      },
      onError: (err, previousTabs, context) => {
        if (context?.previousTabs) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            context.previousTabs,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  update(): UseMutationResult<ITab, Error, IUpdateTabParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<ITab>>(
          `/promotions/tabs/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newTab) => {
        const { id, ...restParams } = newTab;
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "tabs", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        });
        const previousTab = await queryClient.getQueryData<ITab>(
          buildQueryKey({
            queryKey: ["redeemables", "tabs", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        );
        if (previousTab) {
          await queryClient.setQueryData<ITab>(
            buildQueryKey({
              queryKey: ["redeemables", "tabs", "retrieve", id],
              venueID: this.api.selectedVenue,
              params: { id },
            }),
            (old) => {
              return {
                ...old,
                ...restParams,
              };
            },
          );
        }
        return { previousTab };
      },
      onError: async (err, newTab, context) => {
        if (context?.previousTab) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "tabs", "retrieve", newTab.id],
              venueID: this.api.selectedVenue,
              params: { id: newTab.id },
            }),
            context.previousTab,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: ["redeemables", "tabs", "retrieve", variables.id],
        });
      },
    });
  }

  partialUpdate(): UseMutationResult<ITab, Error, Partial<IUpdateTabParams>> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<ITab>>(
          `/promotions/tabs/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newTab) => {
        const { id, ...restParams } = newTab;
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "tabs", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        });
        const previousTab = await queryClient.getQueryData<ITab>(
          buildQueryKey({
            queryKey: ["redeemables", "tabs", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        );
        if (previousTab) {
          await queryClient.setQueryData<ITab>(
            buildQueryKey({
              queryKey: ["redeemables", "tabs", "retrieve", id],
              venueID: this.api.selectedVenue,
              params: { id },
            }),
            (old) => {
              return {
                ...old,
                ...restParams,
              };
            },
          );
        }
        return { previousTab };
      },
      onError: async (err, newTab, context) => {
        if (context?.previousTab) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "tabs", "retrieve", newTab.id],
              venueID: this.api.selectedVenue,
              params: { id: newTab.id },
            }),
            context.previousTab,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: ["redeemables", "tabs", "retrieve", variables.id],
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteTabParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "tabs", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.delete<AxiosResponse<string>>(
          `/promotions/tabs/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data === "";
      },
    });
  }
}
